// src/utils.js

import * as XLSX from "xlsx";
import Papa from "papaparse";
import React, { useEffect, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
// import "ag-grid-community/styles/ag-grid.css";
// import "ag-grid-community/styles/ag-theme-alpine.css";
import { centerCellStyle } from "../styles/theme";
import axios from "axios";

// Converts seconds to "h m s" format
export const formatDuration = (seconds) => {
  if (!seconds) return "0s";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  if (s || parts.length === 0) parts.push(`${s}s`);
  return parts.join(" ");
};

// Format date to YYYY-MM-DD HH:mm
export const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
};

// utils.js

/**
 * Returns a JSX link for HTML report based on filename
 * @param {string} fileName - Name of the file
 * @param {string} filePath - Full URL/path to the file
 * @param {object} linkStyle - Optional CSS style for the link
 * @returns {JSX.Element}
 */
export function getHtmlReportLink(fileName, filePath, linkStyle = {}) {
  const name = fileName.toLowerCase();

  if (name.includes("multiqc")) {
    return (
      <a href={filePath} target="_blank" rel="noopener noreferrer" style={linkStyle}>
        View MultiQC HTML report
      </a>
    );
  }

  if (name.includes("snpeff") || name.includes("variant")) {
    return (
      <a href={filePath} target="_blank" rel="noopener noreferrer" style={linkStyle}>
        View SnpEff Variant Summary
      </a>
    );
  }

  // fallback: just embed in iframe
  return <iframe src={filePath} title={fileName} style={{ width: "100%", height: 500 }} />;
}


/**
 * FileGuard
 * Checks if a file exists before rendering content.
 * If file is missing, shows a clean warning instead of "Cannot GET".
 *
 * Usage:
 * <FileGuard filePath={url}>
 *    <YourComponent />
 * </FileGuard>
 */
export const FileGuard = ({ filePath, children }) => {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let mounted = true;

    const checkFile = async () => {
      try {
        const res = await fetch(filePath, { method: "GET" });

        if (!mounted) return;

        if (res.ok) {
          setStatus("ok");
        } else {
          setStatus("missing");
        }
      } catch (err) {
        if (mounted) setStatus("missing");
      }
    };

    if (filePath) {
      checkFile();
    } else {
      setStatus("missing");
    }

    return () => {
      mounted = false;
    };
  }, [filePath]);

  // 🔹 Extract filename from path
  const fileName = filePath
    ? filePath.split("/").pop()
    : "File";

  if (status === "checking") {
    return <div>Checking {fileName}...</div>;
  }

  if (status === "missing") {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center", // horizontal
          alignItems: "center",     // vertical
          height: "100%",           // parent height
          color: "red",
          fontWeight: 500
        }}
      >
        ⚠ {fileName} is not available
      </div>
    );
  }

  return children;
};
